import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Trash2, Loader2, Clock, Palette, ExternalLink } from "lucide-react";

export default function CreatorsManager({ open, onClose }) {
  const [creators, setCreators] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/creators");
      setCreators(res.data.creators);
      setInvites(res.data.pending_invites);
    } catch {
      toast.error("Failed to load creators");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const res = await api.post("/admin/creators/invite", { email: email.trim() });
      toast.success(res.data.promoted ? "User granted Creator access" : "Creator invite sent");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const revokeCreator = async (c) => {
    try {
      await api.delete(`/admin/creators/${c.user_id}`);
      toast.success(`${c.email} removed as creator`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke");
    }
  };

  const revokeInvite = async (inviteEmail) => {
    try {
      await api.delete(`/admin/creators/invite/${encodeURIComponent(inviteEmail)}`);
      toast.success("Invite revoked");
      load();
    } catch {
      toast.error("Failed to revoke invite");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border border-white/15 rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="creators-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-700 text-2xl tracking-tight text-white uppercase">Creators</DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/40">
            Grant users their own public gallery. Creators manage their own collectibles; you can view all and delete any item.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-8">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-glow-accent">Grant Gallery Access</label>
            <div className="flex gap-2 mt-2">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                placeholder="creator@example.com"
                type="email"
                className="bg-transparent border-white/20 text-white rounded-none focus:border-glow-accent focus-visible:ring-1 focus-visible:ring-glow-accent font-mono"
                data-testid="creator-invite-email-input"
              />
              <Button
                onClick={handleInvite}
                disabled={inviting}
                className="bg-glow-accent text-black font-bold hover:bg-glow-accent/80 rounded-none font-mono text-xs uppercase tracking-wider shrink-0"
                data-testid="creator-invite-submit-btn"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Grant</>}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-glow-accent" /></div>
          ) : (
            <>
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">Creators ({creators.length})</p>
                {creators.length === 0 ? (
                  <p className="font-mono text-sm text-white/40 border border-dashed border-white/15 p-6 text-center">No creators yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="creators-manager-list">
                    {creators.map((c) => (
                      <div key={c.user_id} className="flex items-center justify-between border border-white/10 p-3 gap-3" data-testid={`creator-row-${c.user_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {c.picture ? (
                            <img src={c.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-glow-accent/20 flex items-center justify-center"><Palette className="w-4 h-4 text-glow-accent" /></div>
                          )}
                          <div className="min-w-0">
                            <p className="font-mono text-sm text-white truncate">{c.email}</p>
                            <span className="font-mono text-[10px] uppercase tracking-wider text-glow-accent">{c.item_count} items</span>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Link to={`/profile/${c.user_id}`} onClick={onClose}>
                            <Button variant="outline" size="sm" className="border-glow-primary/50 text-glow-primary hover:bg-glow-primary/10 rounded-none h-8" aria-label="View profile" data-testid={`view-creator-${c.user_id}`}>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                          <Button onClick={() => revokeCreator(c)} variant="outline" size="sm" className="border-glow-secondary/50 text-glow-secondary hover:bg-glow-secondary/10 rounded-none h-8" aria-label="Revoke creator" data-testid={`revoke-creator-${c.user_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {invites.length > 0 && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">Pending Invites ({invites.length})</p>
                  <div className="space-y-2" data-testid="creator-invites-list">
                    {invites.map((inv) => (
                      <div key={inv.email} className="flex items-center justify-between border border-dashed border-white/15 p-3" data-testid={`creator-invite-${inv.email}`}>
                        <span className="font-mono text-sm text-white/70 flex items-center gap-2 min-w-0 truncate">
                          <Clock className="w-3.5 h-3.5 text-glow-accent shrink-0" /> {inv.email}
                        </span>
                        <Button onClick={() => revokeInvite(inv.email)} variant="outline" size="sm" className="border-white/20 text-white/60 hover:bg-white/5 rounded-none shrink-0" aria-label="Revoke invite" data-testid={`revoke-creator-invite-${inv.email}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
